/**
 * Shared helpers for the Knowledge Graph dataset screens.
 *
 * Kept free of JSX so the list, the detail shell and every tab pull from the
 * same vocabulary — status tones, the path grammar, coverage formatting, and the
 * adapters that turn an /inspect response into something the form can render.
 *
 * Schema sniffing and mapping proposals used to live here. They are now
 * POST /kg-datasets/inspect: the server reads the samples through the same
 * decoder the push uses, so its paths are the only ones guaranteed to resolve.
 */

// ── Limits (mirror the server's) ───────────────────────────────────────────

/** KG_PUSH_MAX_BATCH. The client owns the chunking — there is no upload endpoint. */
export const PUSH_MAX_BATCH = 500;
/** KG_GRAPH_MAX_NODES. There is deliberately no whole-dataset mode. */
export const GRAPH_MAX_NODES = 200;
/** `limit` cap on the records table. */
export const RECORDS_MAX_LIMIT = 200;
/** Records sent to dry-run. Free, writes nothing — but keep the payload sane. */
export const DRY_RUN_SAMPLE_SIZE = 50;
/** Dry-run fires on every mapping edit; it is a real request. */
export const DRY_RUN_DEBOUNCE_MS = 500;
/** Poll GET /{id} while indexed < records. Nothing else polls. */
export const INGEST_POLL_MS = 4000;
/** Above this, a browser load is the wrong tool — show the API instead. */
export const SERVER_TO_SERVER_THRESHOLD = 1000000;
/** `skipped[]` in a dry-run/job report is capped server-side. */
export const SKIPPED_REPORT_CAP = 100;
/**
 * Hard server limit on `mapping_config.facets`. Every facet is also prompt the
 * model reads before it can choose one, so the useful number is far below this —
 * but going over it is a 400 rather than a slow answer, so the form stops first.
 */
export const MAX_FACETS = 32;
/**
 * Hard server limit on `mapping_config.text`. A wide export can offer fifty
 * text-ish columns; including them all is a 400, and would in any case assemble
 * a document the model has to read past to find anything.
 */
export const MAX_TEXT_FIELDS = 12;
/** Joins the parts of a composite key; NUL can never occur in real data. */
export const KEY_PART_SEPARATOR = String.fromCharCode(0);

/** Hard cap on `samples` for POST /inspect. The server refuses more. */
export const INSPECT_MAX_SAMPLES = 200;
/** The other /inspect cap: 2 MB of JSON, whichever limit is hit first. */
export const INSPECT_MAX_BYTES = 2 * 1024 * 1024;
/** What we actually send: enough to be representative, small enough to be quick. */
export const INSPECT_SAMPLE_SIZE = 100;

// ── Reading records out of files ───────────────────────────────────────────
// Customer exports do not arrive in one tidy shape. Real ones seen so far:
//   • one file holding an array of records
//   • one file per record — a bare object, thousands of files in a folder
//   • an envelope, {records: [...]} / {items: [...]} / {data: [...]}
//   • JSON Lines — one JSON object per line, no enclosing array
// All four are accepted. Rejecting a per-record export would mean telling a
// customer to go and write a script before they can use the product at all.

const isPlainRecord = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** Normalise any of the accepted shapes into a flat list of record objects. */
export const coerceToRecords = (parsed) => {
  if (Array.isArray(parsed)) return parsed.filter(isPlainRecord);
  if (!isPlainRecord(parsed)) return [];
  for (const key of ['records', 'items', 'data', 'results', 'rows']) {
    if (Array.isArray(parsed[key])) return parsed[key].filter(isPlainRecord);
  }
  // A bare object IS one record — the per-file export shape.
  return [parsed];
};

/**
 * @returns {{records: Array, error: string|null}}
 */
export const parseRecordsFromText = (text, filename = 'file') => {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return { records: [], error: `${filename} is empty.` };

  try {
    const records = coerceToRecords(JSON.parse(trimmed));
    if (records.length === 0) {
      return { records: [], error: `${filename} has no record objects in it.` };
    }
    return { records, error: null };
  } catch (jsonError) {
    // Not one JSON value — try JSON Lines before giving up, since a big export
    // is often written that way precisely to avoid a giant single array.
    const lines = trimmed.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length > 1) {
      const records = [];
      for (const line of lines) {
        try {
          records.push(...coerceToRecords(JSON.parse(line)));
        } catch (lineError) {
          return { records: [], error: `${filename}: ${jsonError.message}` };
        }
      }
      if (records.length > 0) return { records, error: null };
    }
    return { records: [], error: `${filename}: ${jsonError.message}` };
  }
};

/**
 * Read one or many files into a single record list. Per-record exports mean a
 * customer may select hundreds of files at once, so failures are collected
 * rather than thrown — a couple of bad files should not discard the good ones.
 *
 * @param {FileList|File[]} fileList
 * @returns {Promise<{records: Array, errors: string[], fileCount: number}>}
 */
export const readRecordsFromFiles = async (fileList) => {
  const files = Array.from(fileList || []);
  const records = [];
  const errors = [];

  for (const file of files) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const text = await file.text();
      const result = parseRecordsFromText(text, file.name);
      if (result.error) errors.push(result.error);
      else records.push(...result.records);
    } catch (e) {
      errors.push(`${file.name}: couldn't be read (${e.message})`);
    }
  }

  return { records, errors, fileCount: files.length };
};

// ── Response shapes ────────────────────────────────────────────────────────

/**
 * List endpoints across this API vary between a bare array and an envelope
 * (`{datasets: []}`, `{jobs: []}`), so every caller unwraps through here
 * instead of guessing.
 */
export const unwrapList = (payload, ...keys) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const candidates = [...keys, 'items', 'results', 'data'];
  for (const key of candidates) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  // Observed in the wild: the list endpoint answering with the entity itself
  // rather than a list when there is exactly one of them. Rendering "nothing
  // here yet" over a dataset that plainly exists is the worst possible reading
  // of that, so treat a bare entity as a one-element list.
  if (payload.id !== undefined) return [payload];
  return [];
};

// ── Errors ─────────────────────────────────────────────────────────────────

const describeValidationDetail = (detail) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        const message = entry?.msg || entry?.message;
        if (!message) return null;
        const field = Array.isArray(entry?.loc) ? entry.loc[entry.loc.length - 1] : null;
        return field ? `${field}: ${message}` : message;
      })
      .filter(Boolean);
    return parts.length ? parts.join('. ') : null;
  }
  if (detail && typeof detail === 'object') return detail.msg || detail.message || null;
  return null;
};

/**
 * Mapping validation errors come back flat and readable by design
 * (`"Invalid mapping config: facet name 'Issuing Authority' must match …"`)
 * specifically so they can be surfaced verbatim. Do not rewrite them.
 */
export const describeError = (error, fallback = 'Something went wrong.') => {
  if (!error) return fallback;
  if (error.isCanceled) return 'Cancelled.';
  if (error.isTimeout) {
    return 'This is taking longer than expected and may still be running on the server. Refresh in a moment to see the result.';
  }
  if (error.isNetworkError) return "Couldn't reach the server. Check your connection and try again.";
  return (
    error.data?.error ||
    error.data?.message ||
    describeValidationDetail(error.data?.detail) ||
    error.message ||
    fallback
  );
};

/** 404 means "not found OR not yours" — never imply the dataset exists. */
export const isNotFound = (error) => error?.status === 404;

// ── Path grammar ───────────────────────────────────────────────────────────
// Dot-separated segments, `[]` marking a segment that is a list, and — since the
// backend added quoting — backticks around any segment that isn't a plain
// identifier. THE UI OWNS THE QUOTING: an unquoted `tbl.col` is read as
// "field tbl, then field col" and silently resolves to nothing, which the author
// only discovers as 0% coverage much later.

/** A segment that needs no quoting. */
export const BARE_SEGMENT_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const segmentNeedsQuoting = (name) => !BARE_SEGMENT_RE.test(String(name ?? ''));

/** `Issuing Authority` → `` `Issuing Authority` ``; `status` → `status`. */
export const quoteSegment = (name) => {
  const raw = String(name ?? '');
  return segmentNeedsQuoting(raw) ? `\`${raw}\`` : raw;
};

/**
 * Backtick-aware split. A dot INSIDE backticks is part of the name, not a
 * separator — that distinction is the whole point of the quoting.
 * @returns {Array<{name: string, isList: boolean, wasQuoted: boolean}>}
 */
export const parsePath = (path) => {
  const src = String(path ?? '');
  const segments = [];
  let buf = '';
  let inQuote = false;
  let wasQuoted = false;
  let started = false;

  const flush = () => {
    let name = buf;
    let isList = false;
    if (name.endsWith('[]')) {
      isList = true;
      name = name.slice(0, -2);
    }
    segments.push({ name, isList, wasQuoted });
    buf = '';
    wasQuoted = false;
  };

  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    started = true;
    if (inQuote) {
      if (ch === '`') inQuote = false;
      else buf += ch;
    } else if (ch === '`') {
      inQuote = true;
      wasQuoted = true;
    } else if (ch === '.') {
      flush();
    } else {
      buf += ch;
    }
  }
  if (started) flush();
  return segments.filter((s) => s.name !== '' || s.wasQuoted);
};

/** @param {Array<{name: string, isList?: boolean}>} segments */
export const buildPath = (segments) =>
  (segments || [])
    .map((s) => `${quoteSegment(s.name)}${s.isList ? '[]' : ''}`)
    .join('.');

/**
 * Re-quote an existing or hand-typed path. Idempotent — parsing strips the
 * backticks and buildPath only re-adds them where they are actually needed, so
 * running this twice never double-quotes.
 */
export const normalizePath = (path) => buildPath(parsePath(path));

/**
 * Resolve hand-typed text into a real path, checking it against the columns
 * actually present in the sample BEFORE falling back to the grammar.
 *
 * This exists for one case that normalizePath alone cannot fix: `tbl.col` is
 * grammatically valid as "field tbl, then field col", so quoting rules can't
 * tell it apart from a genuine nested path — but if the sample has a column
 * literally named `tbl.col`, the user meant that one, and leaving it unquoted
 * silently resolves to nothing. Matching against known columns first is what
 * makes the dot case safe.
 *
 * @param {Array} fields output of describeSampleFields()
 */
export const resolvePathAgainstFields = (input, fields = []) => {
  const raw = String(input ?? '').trim();
  if (!raw) return '';

  // Already a path we know about, quoted or not.
  const normalized = normalizePath(raw);
  const known = fields.find((f) => f.path === raw || f.path === normalized);
  if (known) return known.path;

  // Does the WHOLE string match one real top-level column, dots and all?
  const exact = fields.find((f) => f.depth === 1 && f.segments[0].name === raw);
  if (exact) return exact.path;

  const caseInsensitive = fields.find(
    (f) => f.depth === 1 && f.segments[0].name.toLowerCase() === raw.toLowerCase()
  );
  if (caseInsensitive) return caseInsensitive.path;

  return normalized;
};

/** Human-readable tail of a path, for labels. */
export const pathLeafName = (path) => {
  const segments = parsePath(path);
  return segments.length ? segments[segments.length - 1].name : String(path ?? '');
};

/** Does any segment carry `[]`? Such a path can hold several values per record. */
export const pathIsList = (path) => parsePath(path).some((s) => s.isList);

// ── record_key (may be composite) ──────────────────────────────────────────
// The upsert key. Accepts one column or an ordered list — order lines, invoice
// items and any child-table export need two or more.

/** Stored as a bare string for one column, an array for several. */
export const serializeRecordKey = (paths) => {
  const list = (paths || []).map((p) => String(p ?? '').trim()).filter(Boolean);
  if (list.length === 0) return '';
  return list.length === 1 ? list[0] : list;
};

/** Both stored shapes hydrate into the same ordered list. */
export const parseRecordKey = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v ?? '').trim()).filter(Boolean);
  const single = String(value ?? '').trim();
  return single ? [single] : [];
};

/**
 * Validated in the form rather than at the 400.
 * @returns {string|null} an error message, or null when valid
 */
export const validateRecordKey = (paths) => {
  const list = (paths || []).map((p) => String(p ?? '').trim()).filter(Boolean);
  if (list.length === 0) {
    return 'Pick at least one column that uniquely identifies a row.';
  }
  const seen = new Set();
  for (const path of list) {
    const normalized = normalizePath(path);
    if (seen.has(normalized)) {
      return `"${path}" is listed twice — each column can only appear once.`;
    }
    seen.add(normalized);
  }
  const listy = list.find((path) => pathIsList(path));
  if (listy) {
    return `"${listy}" is a list — a column that holds several values can't identify a single row.`;
  }
  return null;
};

// ── Facet names ────────────────────────────────────────────────────────────

/** What the model types as a filter key — the API enforces this exactly. */
export const FACET_NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;

export const slugifyFacetName = (raw) => {
  let name = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!name) name = 'facet';
  if (!/^[a-z]/.test(name)) name = `f_${name}`;
  return name.slice(0, 64);
};

/** url-safe dataset key, unique per user. Uniqueness is only knowable from a 400. */
export const slugifyDatasetKey = (raw) =>
  String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

const uniqueName = (base, taken) => {
  if (!taken.has(base)) {
    taken.add(base);
    return base;
  }
  let n = 2;
  while (taken.has(`${base}_${n}`)) n += 1;
  const name = `${base}_${n}`;
  taken.add(name);
  return name;
};

// ── Status ─────────────────────────────────────────────────────────────────

export const DATASET_STATUS = {
  draft: {
    key: 'draft',
    label: 'Draft',
    tone: 'neutral',
    agentsSeeIt: false,
    meaning: 'Being set up. Agents cannot see it yet.',
  },
  active: {
    key: 'active',
    label: 'Active',
    tone: 'success',
    agentsSeeIt: true,
    meaning: 'Live — agents attached to this dataset can query it.',
  },
  paused: {
    key: 'paused',
    label: 'Paused',
    tone: 'warning',
    agentsSeeIt: false,
    meaning: 'Stopped answering, nothing deleted. Browsing still works.',
  },
  archived: {
    key: 'archived',
    label: 'Archived',
    tone: 'neutral',
    agentsSeeIt: false,
    meaning: 'Retired. Agents cannot see it.',
  },
};

export const getDatasetStatusMeta = (dataset) =>
  DATASET_STATUS[dataset?.status] || DATASET_STATUS.draft;

export const isActive = (dataset) => dataset?.status === 'active';

/** Agents get KG tools ONLY for an active dataset. This is the #1 confusion. */
export const agentsCanSee = (dataset) => getDatasetStatusMeta(dataset).agentsSeeIt;

export const recordCount = (dataset) => Number(dataset?.counts?.records || 0);
export const indexedCount = (dataset) => Number(dataset?.counts?.indexed || 0);
export const failedCount = (dataset) => Number(dataset?.counts?.failed || 0);

/**
 * Activation is refused while the dataset is empty: an active empty dataset
 * answers every question with zero, which reads as "the data is missing".
 */
export const canActivate = (dataset) => recordCount(dataset) > 0;

export const activateBlockedReason = (dataset) =>
  canActivate(dataset)
    ? null
    : 'Load some records first — an empty active dataset answers every question with zero, which reads as missing data.';

/** True while Celery is still embedding text: filters work, semantic search doesn't. */
export const isIndexing = (dataset) => indexedCount(dataset) < recordCount(dataset);

/**
 * Text bodies are not retained — they are already chunked into the vector store —
 * so once records exist the (text.path, text.embed) pairs cannot change. Lock
 * the text section rather than letting the user do the work and eat a 400.
 */
export const isTextLocked = (dataset) => recordCount(dataset) > 0;

export const TEXT_LOCK_REASON =
  'Text fields are fixed once records are loaded. The original text is not kept — it is already chunked into the vector store — so changing these is only possible by creating a new dataset, or deleting and re-pushing this one.';

// ── Formatting ─────────────────────────────────────────────────────────────

export const formatNumber = (n) => Number(n || 0).toLocaleString();

/**
 * `record_label` is what the agent says in prose — "12 articles", not
 * "12 records". Worth showing in the builder so the author sees the effect.
 */
export const recordNoun = (dataset, count) => {
  const singular = dataset?.record_label?.singular || 'record';
  const plural = dataset?.record_label?.plural || `${singular}s`;
  return Number(count) === 1 ? singular : plural;
};

export const recordLabel = (dataset, count) =>
  `${formatNumber(count)} ${recordNoun(dataset, count)}`;

/**
 * The fraction of records where a facet is populated at all. Render it next to
 * EVERY count derived from that facet: at 48%, "1,204 SEBI papers" means 1,204
 * of the records that say who issued them — the true total is unknowable.
 */
export const formatCoverage = (pct) => {
  const value = Number(pct);
  if (!Number.isFinite(value)) return '—';
  return `${value % 1 === 0 ? value : value.toFixed(1)}%`;
};

export const coverageTone = (pct) => {
  const value = Number(pct);
  if (!Number.isFinite(value)) return 'neutral';
  if (value <= 0) return 'danger';
  if (value < 40) return 'warning';
  if (value < 90) return 'neutral';
  return 'success';
};

export const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Shorten a sample value for a table cell without hiding that it was cut. */
export const truncateValue = (value, max = 40) => {
  const s = String(value ?? '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

export const formatBytes = (bytes) => {
  const n = Number(bytes || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// ── Records browsing ───────────────────────────────────────────────────────

export const ORDER_BY_OPTIONS = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'title', label: 'Title (A–Z)' },
  { value: 'recent', label: 'Recently pushed' },
];

/** `ingest_status` per row answers "why can't the agent find what I just pushed?" */
export const INGEST_STATUS_META = {
  pending: {
    label: 'Pending',
    tone: 'neutral',
    help: 'Committed — filters and counts already include it. Text not embedded yet.',
  },
  indexing: {
    label: 'Indexing',
    tone: 'neutral',
    help: 'Text is being embedded. Semantic search will find it shortly.',
  },
  indexed: {
    label: 'Indexed',
    tone: 'success',
    help: 'Fully searchable — by filter and by meaning.',
  },
  failed: {
    label: 'Failed',
    tone: 'danger',
    help: 'Indexing failed. Check the ingest history for the reason.',
  },
  skipped: {
    label: 'Skipped',
    tone: 'warning',
    help: 'No embeddable text on this record.',
  },
};

export const getIngestStatusMeta = (status) =>
  INGEST_STATUS_META[status] || { label: status || 'Unknown', tone: 'neutral', help: '' };

/**
 * Values within one facet are OR'd, separate facets AND'd. Sent as a JSON string
 * — axios URL-encodes the param. Facet values are matched on their NORMALISED
 * form, so send the catalog's strings verbatim: no client-side lower/trim.
 */
export const buildFiltersParam = (filters) => {
  if (!filters) return undefined;
  const cleaned = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length === 1) cleaned[key] = value[0];
      else if (value.length > 1) cleaned[key] = value;
    } else if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value;
    }
  });
  if (Object.keys(cleaned).length === 0) return undefined;
  return JSON.stringify(cleaned);
};

export const hasActiveFilters = (filters) => Boolean(buildFiltersParam(filters));

// ── Batching ───────────────────────────────────────────────────────────────

/** Slice into ≤500-record batches. They must then be pushed SEQUENTIALLY. */
export const chunkRecords = (records, size = PUSH_MAX_BATCH) => {
  const list = Array.isArray(records) ? records : [];
  const batches = [];
  for (let i = 0; i < list.length; i += size) {
    batches.push(list.slice(i, i + size));
  }
  return batches;
};

/** Running totals across batches. `unchanged` is a NORMAL outcome, not an error. */
export const emptyPushTotals = () => ({ received: 0, ingested: 0, unchanged: 0, failed: 0 });

export const addPushCounts = (totals, counts) => ({
  received: totals.received + Number(counts?.received || 0),
  ingested: totals.ingested + Number(counts?.ingested || 0),
  unchanged: totals.unchanged + Number(counts?.unchanged || 0),
  failed: totals.failed + Number(counts?.failed || 0),
});

// ── Date parsing ───────────────────────────────────────────────────────────

/**
 * `auto` is the default worth reaching for on anything assembled from more than
 * one source: it decides numeric-vs-ISO PER VALUE, so a column that is an epoch
 * on some rows and an ISO string on others parses either way instead of failing
 * half the file.
 */
export const DATE_KIND_OPTIONS = [
  { value: 'auto', label: 'Detect per value', hint: 'Numeric or ISO, decided row by row. Best for mixed exports.' },
  { value: 'iso', label: 'ISO date', hint: '2026-02-11 / 2026-02-11T09:00:00Z' },
  { value: 'unix', label: 'Unix timestamp', hint: 'A number of seconds, milliseconds or microseconds.' },
  { value: 'strptime', label: 'Custom pattern', hint: 'Anything else — give a strptime pattern.' },
];

/**
 * Only meaningful for numeric timestamps, so shown for `unix` and `auto`.
 * Microseconds is not exotic: BigQuery and GA4 `event_timestamp` are `us`, and
 * read as milliseconds they land some fifty thousand years in the future.
 */
export const DATE_UNIT_OPTIONS = [
  { value: 'auto', label: 'Detect from magnitude' },
  { value: 's', label: 'Seconds' },
  { value: 'ms', label: 'Milliseconds' },
  { value: 'us', label: 'Microseconds — BigQuery / GA4' },
];

/** The kinds for which a numeric unit applies at all. */
export const dateKindUsesUnit = (kind) => kind === 'unix' || kind === 'auto';

/**
 * Offered as a choice at create time because the consequence is invisible and
 * permanent-feeling: a record at 2026-01-31 20:00 UTC is FEBRUARY in IST, and
 * "articles from February" has to mean February to the person reading the
 * answer. Left at 0 for an Indian customer, every month boundary misfiles.
 */
export const TIMEZONE_OPTIONS = [
  { value: 0, label: 'UTC (+00:00)' },
  { value: 330, label: 'India — IST (+05:30)' },
  { value: -300, label: 'US Eastern (−05:00)' },
  { value: -480, label: 'US Pacific (−08:00)' },
  { value: 60, label: 'Central Europe (+01:00)' },
  { value: 240, label: 'Gulf — GST (+04:00)' },
  { value: 480, label: 'Singapore / China (+08:00)' },
  { value: 540, label: 'Japan / Korea (+09:00)' },
];

// ── Input normalization (mapping_config.preprocessing) ─────────────────────
// Customer exports are not flat. An event feed arrives with the interesting
// fields inside a JSON STRING column; a warehouse export spreads the same
// logical field across three different columns depending on the source.
// `preprocessing` is how that is dealt with — server-side, on the way in.
//
//   namespace   where derived values land. Originals are never overwritten, so
//               `normalized.event_id` and `event_id` can both exist.
//   decode[]    path → target: parse a JSON string column into a real object.
//   fields[]    derived fields with ordered fallback candidates: try A, else B,
//               else C.
//
// Anything derived is then addressable as `<namespace>.<name>` in record_key,
// title, text[], dates[] and facets[].

export const PREPROCESSING_NAMESPACE = 'normalized';

export const DERIVED_MODES = [
  { value: 'first', label: 'First — use the first one found' },
  { value: 'join', label: 'Join — glue the parts together' },
];

export const DERIVED_CASES = [
  { value: '', label: 'Leave as is' },
  { value: 'lower', label: 'lowercase' },
  { value: 'upper', label: 'UPPERCASE' },
];

/**
 * The only fallback the server implements: hash the record's content and use
 * that as the value. It exists for the record_key case — an event export where
 * some rows genuinely carry no id, and dropping them would be worse than giving
 * them a stable synthetic one.
 */
export const DERIVED_FALLBACKS = [
  { value: '', label: 'None — leave the field empty' },
  { value: 'content_hash', label: 'Hash of the record’s content' },
];

export const emptyDecodeEntry = () => ({ path: '', target: '' });

export const emptyCandidate = () => ({ path: '' });

export const emptyDerivedField = () => ({
  name: '',
  mode: 'first',
  separator: ' ',
  case: '',
  required: false,
  fallback: '',
  max_chars: null,
  candidates: [emptyCandidate()],
});

export const emptyPreprocessing = () => ({
  namespace: PREPROCESSING_NAMESPACE,
  decode: [],
  fields: [],
});

/** The namespace a config's derived values live under. */
export const preprocessingNamespace = (config) =>
  String(config?.preprocessing?.namespace || PREPROCESSING_NAMESPACE);

/**
 * Is preprocessing actually doing anything? A block with a namespace and two
 * empty arrays is inert, and treating that as "in use" would lock `record_key`
 * on a dataset that never derived a thing.
 */
export const hasPreprocessing = (config) => {
  const pre = config?.preprocessing;
  if (!pre || typeof pre !== 'object') return false;
  const decode = Array.isArray(pre.decode) ? pre.decode : [];
  const fields = Array.isArray(pre.fields) ? pre.fields : [];
  return decode.some((d) => d?.path) || fields.some((f) => f?.name);
};

/** `normalized.event_id`, correctly quoted if the name needs it. */
export const derivedPath = (config, name) =>
  buildPath([
    { name: preprocessingNamespace(config) },
    { name: String(name || '') },
  ]);

/** Does this path read from the preprocessing namespace rather than the raw record? */
export const isDerivedPath = (config, path) => {
  const segments = parsePath(path);
  return segments.length > 1 && segments[0].name === preprocessingNamespace(config);
};

/** True when the upsert key depends on a value only the server can derive. */
export const recordKeyIsDerived = (config) =>
  parseRecordKey(config?.record_key).some((path) => isDerivedPath(config, path));

/**
 * Preprocessing decides record IDENTITY and what text gets embedded, so like the
 * text section it is frozen once records exist — changing it would silently
 * re-key everything already loaded.
 */
export const isPreprocessingLocked = (dataset) => recordCount(dataset) > 0;

export const PREPROCESSING_LOCK_REASON =
  'Input normalization is fixed once records are loaded. It decides how each record is identified and what text is searchable, so changing it would re-key data that is already in — the server rejects it. Create a new dataset, or delete this one and re-push.';

/**
 * `record_key` is normally editable — it only changes what the NEXT push
 * updates. With preprocessing in play it isn't: the key is derived, so editing
 * it means editing the derivation, which is itself frozen.
 */
export const isRecordKeyLocked = (dataset, config) =>
  recordCount(dataset) > 0 && hasPreprocessing(config);

export const RECORD_KEY_LOCK_REASON =
  'The row identity is derived by input normalization, and that is fixed once records are loaded. The server rejects a change here.';

/**
 * Mirrors the server's validation so a mistake is caught in the form.
 * @returns {string[]} readable problems
 */
export const validatePreprocessing = (config) => {
  const errors = [];
  const pre = config?.preprocessing;
  if (!pre || typeof pre !== 'object') return errors;

  const decode = Array.isArray(pre.decode) ? pre.decode : [];
  decode.forEach((entry, i) => {
    if (!String(entry?.path || '').trim()) {
      errors.push(`Decode ${i + 1}: pick the column holding the JSON string.`);
    }
    if (!String(entry?.target || '').trim()) {
      errors.push(`Decode ${i + 1}: give the decoded object a name.`);
    }
  });

  const fields = Array.isArray(pre.fields) ? pre.fields : [];
  const seen = new Set();
  fields.forEach((field, i) => {
    const name = String(field?.name || '').trim();
    if (!name) {
      errors.push(`Derived field ${i + 1}: needs a name.`);
    } else if (!FACET_NAME_RE.test(name)) {
      errors.push(
        `Derived field "${name}" must be lowercase letters, digits and underscores, starting with a letter.`
      );
    } else if (seen.has(name)) {
      errors.push(`Two derived fields are both called "${name}".`);
    } else {
      seen.add(name);
    }

    const candidates = Array.isArray(field?.candidates) ? field.candidates : [];
    if (candidates.length === 0) {
      errors.push(`Derived field "${name || i + 1}" has no candidate columns to read from.`);
    }
    candidates.forEach((candidate, c) => {
      if (!String(candidate?.path || '').trim()) {
        errors.push(`Derived field "${name || i + 1}", candidate ${c + 1}: pick a column.`);
      }
    });
  });

  return errors;
};

/** Strip the editor's empty scaffolding so a saved config stays readable. */
export const serializePreprocessing = (pre) => {
  if (!pre || typeof pre !== 'object') return undefined;
  const decode = (Array.isArray(pre.decode) ? pre.decode : [])
    .filter((d) => String(d?.path || '').trim() && String(d?.target || '').trim())
    .map((d) => ({ path: String(d.path).trim(), target: String(d.target).trim() }));

  const fields = (Array.isArray(pre.fields) ? pre.fields : [])
    .filter((f) => String(f?.name || '').trim())
    .map((f) => {
      const out = {
        name: String(f.name).trim(),
        mode: f.mode === 'join' ? 'join' : 'first',
        candidates: (Array.isArray(f.candidates) ? f.candidates : [])
          .filter((c) => String(c?.path || '').trim())
          .map((c) => {
            const candidate = { path: String(c.path).trim() };
            // `when` / `select` / `date` are passed straight back through. Their
            // shapes are the server's, so re-deriving them here would only lose
            // whatever a richer config already carries.
            if (c.when !== undefined && c.when !== null) candidate.when = c.when;
            if (c.select !== undefined && c.select !== null) candidate.select = c.select;
            if (c.date !== undefined && c.date !== null) candidate.date = c.date;
            return candidate;
          }),
      };
      if (out.mode === 'join') out.separator = f.separator ?? ' ';
      if (f.case) out.case = f.case;
      if (f.required) out.required = true;
      if (f.fallback) out.fallback = f.fallback;
      if (f.max_chars) out.max_chars = Number(f.max_chars);
      return out;
    });

  if (decode.length === 0 && fields.length === 0) return undefined;
  return {
    namespace: String(pre.namespace || PREPROCESSING_NAMESPACE),
    decode,
    fields,
  };
};

// ── Reading an /inspect response ───────────────────────────────────────────
// The server owns the schema now, so these adapters stay tolerant: a key it
// renames should degrade to "not shown", never to a crash.

const firstDefined = (source, keys, fallback) => {
  for (const key of keys) {
    if (source && source[key] !== undefined && source[key] !== null) return source[key];
  }
  return fallback;
};

/**
 * Does the server mark its picks in this list?
 *
 * Checked once per list so a list that carries the flag is trusted completely,
 * and one that does not falls back to a semantic rule — never to list position,
 * which orders a list for reading and says nothing about what was chosen.
 */
const hasRecommendedFlag = (list) =>
  (Array.isArray(list) ? list : []).some(
    (entry) => entry && typeof entry === 'object' && entry.recommended !== undefined
  );

/** The server's own flag on one entry. */
const isRecommended = (entry) =>
  Boolean(entry && typeof entry === 'object' && entry.recommended);

/** A count, whether the server sent the number or the values it counted. */
const countOf = (value) => {
  if (Array.isArray(value)) return value.length;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Turn `fields[]` from /inspect into the descriptors the column pickers and the
 * mapping form already consume.
 *
 * The paths are used VERBATIM. They are quoted the way the server quotes them,
 * and — critically — derived alias names like `payload_1` are assigned per
 * inspection, so a path is only meaningful next to the `preprocessing` block
 * that came back with it.
 */
export const fieldsFromInspect = (inspection) => {
  const raw = Array.isArray(inspection?.fields) ? inspection.fields : [];
  return raw
    .map((field) => {
      const path = String(firstDefined(field, ['path', 'name'], '') || '');
      if (!path) return null;
      const segments = parsePath(path);
      const coveragePct = Number(
        firstDefined(field, ['coverage_pct', 'coverage_percent', 'coverage'], 0)
      );
      const examples = firstDefined(
        field,
        ['examples', 'example_values', 'sample_values', 'values'],
        []
      );
      const types = firstDefined(field, ['types', 'type'], []);
      const typeList = Array.isArray(types) ? types : [types].filter(Boolean);
      const multiValued = Boolean(
        firstDefined(field, ['multi_valued', 'multiValued', 'is_list', 'repeated'], false) ||
          segments.some((s) => s.isList)
      );
      return {
        path,
        segments,
        leaf: segments.length ? segments[segments.length - 1].name : path,
        depth: segments.length,
        isList: segments.some((s) => s.isList),
        multiValued,
        // Coverage arrives as a percentage already; a 0–1 fraction is coerced so
        // an older shape doesn't render every column as "0%".
        coveragePct: coveragePct > 0 && coveragePct <= 1 ? coveragePct * 100 : coveragePct,
        // `distinct_values` is the name the server actually uses. It arrives as
        // a COUNT, but the name reads like a list and one may well appear as
        // one, so both are accepted — this column is how a reader tells a key
        // apart from a category, and rendering every row as "—" makes the whole
        // table unusable for that.
        distinct: countOf(
          firstDefined(
            field,
            ['distinct_values', 'distinct', 'distinct_count', 'cardinality', 'n_distinct'],
            0
          )
        ),
        values: (Array.isArray(examples) ? examples : [examples]).filter(
          (v) => v !== null && v !== undefined
        ),
        types: typeList,
        type: typeList[0] || '',
        // The value list was cut short, so `distinct` is a floor. Rendered as
        // "18+" rather than as a total.
        distinctTruncated: Boolean(
          firstDefined(field, ['distinct_values_truncated', 'distinct_truncated'], false)
        ),
        derived: Boolean(firstDefined(field, ['derived', 'is_derived'], false)),
        // Set once a combined field reads from this column. It is then part of
        // something better and should no longer be offered on its own.
        coveredBy: firstDefined(field, ['covered_by', 'coveredBy'], null),
        source: firstDefined(field, ['source', 'source_path'], null),
        needsQuoting: segments.some((s) => segmentNeedsQuoting(s.name)),
      };
    })
    .filter(Boolean);
};

/**
 * The row-identity candidates the server proposed, best first.
 *
 * Each entry is a WHOLE key — `paths` is a list because a key can be composite
 * (order lines and invoice items need two columns together). The entries are
 * ALTERNATIVES to each other, so choosing one replaces the choice.
 *
 * Which one to preselect is answered by `recommended`, never by being first.
 *
 * Nothing is filtered out: a title can legitimately be unique, and hiding the
 * unconventional candidates would leave an export with no id column showing no
 * options at all.
 *
 * @returns {Array<{paths, label, recommended, nameMatchesConvention,
 *   uniqueInSample, coveragePct, distinct}>} in the server's own order
 */
export const recordKeyCandidatesFromInspect = (inspection) => {
  const suggestions = inspection?.suggestions || {};
  const raw = firstDefined(suggestions, ['record_keys', 'record_key'], []);
  const list = Array.isArray(raw) ? raw : [raw];
  const byPath = new Map((inspection?.fields || []).map((f) => [f.path, f]));
  const keyFlagged = hasRecommendedFlag(list);

  return list
    .map((entry) => {
      // Each entry is a WHOLE key, and `paths` is a list because a key can be
      // composite — order lines and invoice items need two columns together.
      // The singular spellings are accepted too, but `paths` is what the server
      // actually sends.
      const raw_paths = firstDefined(
        typeof entry === 'object' ? entry : {},
        ['paths', 'path'],
        typeof entry === 'string' ? entry : null
      );
      const paths = (Array.isArray(raw_paths) ? raw_paths : [raw_paths])
        .map((path) => (typeof path === 'string' ? path.trim() : ''))
        .filter(Boolean);
      if (paths.length === 0) return null;

      const meta = typeof entry === 'object' ? entry : {};
      const fields = paths.map((path) => byPath.get(path)).filter(Boolean);
      return {
        paths,
        recommended: isRecommended(meta),
        // Whether the SERVER expressed an opinion at all, so an older response
        // can fall back without a newer one being second-guessed.
        serverFlagged: keyFlagged,
        // A composite reads as "a + b" everywhere it is shown.
        label: paths.join(' + '),
        nameMatchesConvention: Boolean(
          firstDefined(meta, ['name_matches_convention', 'nameMatchesConvention'], false)
        ),
        // "Unique across the records we looked at" — NOT proof of uniqueness in
        // production, which is what the server's own warning says out loud.
        uniqueInSample: Boolean(
          firstDefined(meta, ['unique_in_sample', 'uniqueInSample'], false)
        ),
        // A composite is only as populated as its least-populated part.
        coveragePct: Number(
          firstDefined(
            meta,
            ['coverage_pct', 'coverage'],
            fields.length ? Math.min(...fields.map((f) => f.coveragePct || 0)) : 0
          )
        ),
        distinct: fields.length ? Math.max(...fields.map((f) => f.distinct || 0)) : 0,
      };
    })
    .filter(Boolean);
  // Deliberately NOT re-sorted — the server's order is for reading, and which
  // one is the PICK is answered by `recommended`, not by being first.
};

/**
 * The candidate to preselect: the one the server marked.
 *
 * Falls back only when no entry in the list carries the flag at all, and then to
 * a semantic rule — an id-shaped column that was unique across the sample —
 * rather than to whichever happened to be listed first.
 */
export const pickAllRecommended = (candidates, fallback) => {
  const list = Array.isArray(candidates) ? candidates : [];
  const flagged = list.filter((c) => c.recommended);
  if (flagged.length > 0) return flagged;
  // The server answered and marked none — respect that instead of falling back
  // to "everything on offer", which is how a 729-column export ends up with 96
  // facets and 48 text fields nobody chose.
  if (list.some((c) => c.serverFlagged)) return [];
  return typeof fallback === 'function' ? list.filter(fallback) : [];
};

export const pickRecommended = (candidates, fallback) => {
  const list = Array.isArray(candidates) ? candidates : [];
  if (list.length === 0) return null;
  const flagged = list.find((c) => c.recommended);
  if (flagged) return flagged;
  // The server answered and the answer was "none of these" — respect that
  // rather than promoting whichever happens to be first.
  if (list.some((c) => c.serverFlagged)) return null;
  return (typeof fallback === 'function' ? list.find(fallback) : null) || null;
};

/**
 * Text candidates — the fields that could carry a record's prose.
 *
 * `embed` is the money setting in the whole config: it decides whether a
 * 100-page document is vectorised (findable by meaning, and billed for) or
 * merely stored (readable — the agent filters first, then reads the record
 * directly — and free). At 10M records that is the difference between roughly
 * $150 and $975, and it is FROZEN the moment the first record lands.
 *
 * So the default follows the server's `large` flag: a big body is included but
 * left unembedded, and the reason is spelled out next to the switch rather than
 * left as a silent cost.
 *
 * @returns {Array<{path, label, embed, large, coveragePct, maxCharsSeen,
 *   html, always, recommended}>} in the server's order — index 0 is the
 *   title-shaped candidate.
 */
export const textCandidatesFromInspect = (inspection) => {
  const suggestions = inspection?.suggestions || {};
  const raw = Array.isArray(suggestions.text) ? suggestions.text : [];
  const byPath = new Map((inspection?.fields || []).map((f) => [f.path, f]));
  const flagged = hasRecommendedFlag(raw);

  return raw
    .map((entry, index) => {
      const path = typeof entry === 'string' ? entry : entry?.path || '';
      if (!path) return null;
      const field = byPath.get(path);
      const meta = typeof entry === 'object' ? entry : {};
      const large = Boolean(firstDefined(meta, ['large', 'is_large'], false));
      // `title` | `body`. The title-shaped field is the one worth re-stamping
      // onto every chunk, and it is also what `mapping.title` is taken from.
      const role = String(firstDefined(meta, ['suggested_role', 'role'], '') || '');
      const suggestedEmbed = firstDefined(meta, ['suggested_embed', 'embed'], undefined);
      return {
        path,
        role,
        label: entry?.label || humanizeLabel(pathLeafName(path)),
        // The server suggests embedding nearly everything, because it is not
        // costing the bill. `large` is the field where that matters: a 23K-char
        // body vectorised across 10M records is the difference between roughly
        // $150 and $975, and the agent can still READ it after a filter finds
        // the record. So a large body starts stored-not-embedded whatever the
        // server suggests, with the reason next to the switch and one click to
        // change it.
        embed: large ? false : suggestedEmbed === undefined ? true : Boolean(suggestedEmbed),
        serverSuggestedEmbed: suggestedEmbed === undefined ? null : Boolean(suggestedEmbed),
        large,
        coveragePct: Number(
          firstDefined(meta, ['coverage_pct', 'coverage'], field?.coveragePct ?? 0)
        ),
        maxCharsSeen: Number(
          firstDefined(meta, ['max_chars_seen', 'maxCharsSeen', 'max_chars'], 0)
        ),
        html: Boolean(entry?.html),
        // Re-stamped onto every chunk. Only meaningful for the title-shaped
        // entry: a chunk cut from the middle of a long body is otherwise
        // unattributable.
        always: entry?.always !== undefined ? Boolean(entry.always) : role === 'title',
        // The server's flag when it sends one; otherwise the semantic role.
        // Never "it was listed first".
        recommended: flagged ? isRecommended(meta) : role === 'title',
        serverFlagged: flagged,
        order: index,
      };
    })
    .filter(Boolean);
};

/** Turn a chosen text candidate into a `text[]` entry for the config. */
export const textFromCandidate = (candidate, orderIndex = 0, forceEmbed = false) => ({
  path: candidate.path,
  label: candidate.label,
  priority: orderIndex + 1,
  embed: forceEmbed ? true : Boolean(candidate.embed),
  html: Boolean(candidate.html),
  always: Boolean(candidate.always),
});

/**
 * Facet candidates — offered, never pre-selected.
 *
 * The server proposes every field that COULD be filtered on, which on a real
 * export is twenty-odd of which perhaps six are worth having. Auto-adding all of
 * them produces a catalog where the model is offered `article_number` (one value
 * per record, so filtering on it returns exactly one) alongside two copies of the
 * same column under different names — and every one of those costs prompt space
 * and makes the useful facets harder for the model to find.
 *
 * So this annotates rather than filters: each candidate carries a reason when it
 * is a poor choice, and the author ticks what they want. Nothing is hidden — the
 * reasons are advice, and a dataset where `content_type` really is the useful
 * filter is one tick away.
 *
 * @param {object} mapping the mapping so far — its record_key, dates, text and
 *   relations are what make a candidate redundant
 * @returns {Array<{path, name, label, kind, cardinality, coveragePct, distinct,
 *   values, reason: string|null}>}
 */
export const facetCandidatesFromInspect = (inspection, mapping = {}) => {
  const suggestions = inspection?.suggestions || {};
  const raw = Array.isArray(suggestions.facets) ? suggestions.facets : [];
  const byPath = new Map((inspection?.fields || []).map((f) => [f.path, f]));
  const inspectedCount = Number(inspection?.inspectedCount || 0);
  const facetsFlagged = hasRecommendedFlag(raw);

  const keyPaths = new Set(parseRecordKey(mapping.record_key));
  const datePaths = new Set((mapping.dates || []).map((d) => d?.path).filter(Boolean));
  const textPaths = new Set((mapping.text || []).map((t) => t?.path).filter(Boolean));
  const relationPaths = new Set(
    [
      ...(mapping.relations || []).map((r) => r?.path),
      ...(Array.isArray(suggestions.relations) ? suggestions.relations : []).map((r) =>
        typeof r === 'string' ? r : r?.path
      ),
    ].filter(Boolean)
  );

  // Two columns carrying byte-identical values are one column twice — a real
  // shape in exports that publish the same taxonomy under a legacy name and a
  // current one. Signature is the example set plus the distinct count, both of
  // which come from the server.
  const signatures = new Map();

  const names = new Set();
  return raw
    .map((entry, index) => {
      const path = typeof entry === 'string' ? entry : entry?.path || '';
      if (!path) return null;
      const field = byPath.get(path);
      const coveragePct = Number(
        firstDefined(
          typeof entry === 'object' ? entry : {},
          ['coverage_pct', 'coverage'],
          field?.coveragePct ?? 0
        )
      );
      // The entry carries its own counts now; the field table is the fallback.
      const distinct = countOf(
        firstDefined(
          typeof entry === 'object' ? entry : {},
          ['distinct_values', 'distinct'],
          field?.distinct ?? 0
        )
      );
      const values = field?.values || [];
      const meta = typeof entry === 'object' ? entry : {};
      const recommended = facetsFlagged ? isRecommended(meta) : false;
      // `exact_lookup` means the values are near-unique — useful for finding one
      // record by id, not for grouping. `filter_group` is the shape a facet
      // wants. Shown as context, never as a veto.
      const suggestedUse = String(firstDefined(meta, ['suggested_use', 'use'], '') || '');

      let reason = null;
      if (field?.coveredBy) {
        reason = `Already part of the combined field ${field.coveredBy}, which covers more records than this column alone.`;
      } else if (keyPaths.has(path)) {
        reason = 'This is the row identity — one value per record, so filtering on it returns one record.';
      } else if (datePaths.has(path)) {
        reason = 'Already mapped as a date. Dates are filtered by range, not as a value list.';
      } else if (relationPaths.has(path)) {
        reason = 'This looks like a link to other records, so it belongs in Relations rather than as a filter.';
      } else if (textPaths.has(path)) {
        reason = 'Already mapped as text.';
      } else if (coveragePct === 0) {
        reason = 'Empty in every record inspected — this facet would never populate.';
      } else if (inspectedCount > 2 && distinct >= inspectedCount) {
        // Only claimable when we know how many records were looked at; without
        // that denominator "distinct > 2" would condemn every real facet.
        reason = 'A different value on every record, so it groups nothing.';
      }

      const signature = `${distinct}::${JSON.stringify(
        [...values].map((v) => String(v)).sort()
      )}`;
      if (!reason && values.length > 0 && signatures.has(signature)) {
        reason = `Same values as ${signatures.get(signature)} — keeping both gives the model two names for one filter.`;
      } else if (values.length > 0 && !signatures.has(signature)) {
        signatures.set(signature, path);
      }
      if (!reason && suggestedUse === 'exact_lookup') {
        reason =
          'Suggested for looking up one record by value rather than for grouping — as a filter it narrows to very few records.';
      }
      // A reason is an explanation for something the server did NOT pick. Saying
      // "poor choice" about a field it recommended would be the UI arguing with
      // the API in front of the customer.
      if (recommended) reason = null;

      return {
        path,
        recommended,
        serverFlagged: facetsFlagged,
        suggestedUse,
        name: uniqueName(slugifyFacetName(entry?.name || pathLeafName(path)), names),
        label: entry?.label || humanizeLabel(pathLeafName(path)),
        kind: entry?.kind || (pathIsList(path) ? 'nested' : 'scalar'),
        cardinality:
          entry?.cardinality || (pathIsList(path) || field?.multiValued ? 'many' : 'one'),
        coveragePct,
        distinct,
        values,
        note: entry?.note || '',
        order: index,
        reason,
      };
    })
    .filter(Boolean)
    // Recommended first, then the ones with no objection, then the rest. Within
    // a group the server's own order is kept.
    .sort(
      (a, b) =>
        Number(b.recommended) - Number(a.recommended) ||
        Number(Boolean(a.reason)) - Number(Boolean(b.reason)) ||
        a.order - b.order
    );
};

/**
 * Can this config be dry-run at all?
 *
 * `record_key` is mandatory server-side — sending `""` is a 400, not a report,
 * so a dry-run fired before the author has picked one spends a round trip to
 * tell them nothing they could act on. Gate the button on this rather than
 * surfacing the 400.
 *
 * @returns {string|null} why not, or null when it is ready
 */
export const dryRunBlockedReason = (config) => {
  const paths = parseRecordKey(config?.record_key);
  if (paths.length === 0) {
    return 'Pick the column that uniquely identifies a row first — the check cannot run without it.';
  }
  return validateRecordKey(paths);
};

/**
 * Things that make a dataset quietly disappointing rather than broken. The
 * server returns both as dry-run warnings; these exist so the load screen can
 * say them in its own words at the point of no return.
 *
 * Neither BLOCKS a load: a count-only dataset with no embedded text is a
 * legitimate thing to build.
 */
export const mappingGaps = (config) => {
  const gaps = [];
  if (!String(config?.title || '').trim()) {
    gaps.push(
      'No title column is set, so every record shows as a bare id — in the record list, in the graph, and in the agent’s answers.'
    );
  }
  const text = Array.isArray(config?.text) ? config.text : [];
  if (text.length === 0 || !text.some((t) => t?.embed)) {
    gaps.push(
      'No text field is embedded, so semantic search over this dataset returns nothing. Filters and counts still work — build it this way deliberately, not by accident.'
    );
  }
  if ((config?.facets || []).length === 0) {
    gaps.push(
      'No facets are mapped, so the agent cannot filter, count or group these records at all — only search them as documents.'
    );
  }
  return gaps;
};

/** Turn a chosen candidate into a facet entry for the config. */
export const facetFromCandidate = (candidate, orderIndex = 0) => ({
  name: candidate.name,
  label: candidate.label,
  path: candidate.path,
  kind: candidate.kind,
  // Wrong cardinality makes every coverage percentage over this facet wrong.
  cardinality: candidate.cardinality,
  order_index: orderIndex + 1,
  enabled: true,
  note: candidate.note || '',
  normalize: {
    trim: true,
    collapse_ws: true,
    case: null,
    max_len: 500,
    // Left empty on purpose. Merging spellings needs the real values in front of
    // the author, which is the facet editor's job.
    aliases: {},
  },
});

/**
 * Build a starter mapping_config out of `suggestions`.
 *
 * Mostly a TRANSCRIPTION of the server's proposal. Three things are decided here
 * rather than taken as-is, and each is a case where the list the server returns
 * is a shortlist rather than an answer:
 *
 *   • `record_keys[]` are alternatives, so ONE is taken — composing all of them
 *     into a composite key would key the dataset on `article_number + title`.
 *   • the primary date is the best-COVERED date, not the first one listed. It
 *     drives every range filter and month bucket, so a date present on half the
 *     records silently loses the other half.
 *   • facets are NOT taken at all — they come back from
 *     facetCandidatesFromInspect() for the author to choose from.
 */
export const mappingFromInspect = (inspection, { key, label, description } = {}) => {
  const suggestions = inspection?.suggestions || {};
  const preprocessing = inspection?.preprocessing || null;
  const byPath = new Map((inspection?.fields || []).map((f) => [f.path, f]));

  // The candidate the server marked. One candidate is a WHOLE key, so a
  // composite arrives intact rather than being reassembled here.
  const bestKey = pickRecommended(
    recordKeyCandidatesFromInspect(inspection),
    // Only reached on a response with no `recommended` anywhere: an id-shaped
    // column that was unique across the sample. Still not "the first one".
    (candidate) => candidate.nameMatchesConvention && candidate.uniqueInSample
  );
  const recordKeyPaths = bestKey ? bestKey.paths : [];

  // There is no `suggestions.title` — the title is the text candidate whose
  // `suggested_role` is "title". Reading a top-level key that never arrives is
  // what left every record showing as a bare id.
  const textCandidates = textCandidatesFromInspect(inspection);
  const titleCandidate =
    textCandidates.find((c) => c.role === 'title') ||
    pickRecommended(textCandidates) ||
    null;
  const title =
    firstDefined(suggestions, ['title'], '') || (titleCandidate ? titleCandidate.path : '');

  const rawDates = Array.isArray(suggestions.dates) ? suggestions.dates : [];
  const dateNames = new Set();
  const dates = rawDates
    .map((entry) => {
      const path = typeof entry === 'string' ? entry : entry?.path || '';
      const parse = entry?.parse || {};
      return {
        name: uniqueName(slugifyFacetName(entry?.name || pathLeafName(path)), dateNames),
        path,
        primary: false,
        coveragePct: Number(
          firstDefined(
            typeof entry === 'object' ? entry : {},
            ['coverage_pct', 'coverage'],
            byPath.get(path)?.coveragePct ?? 0
          )
        ),
        recommended: isRecommended(entry),
        parse: {
          // `auto` decides numeric-vs-ISO per value, which is what a mixed-source
          // export needs; it is the safe default when the server didn't say.
          kind: parse.kind || 'auto',
          unit: parse.unit || 'auto',
          tz_offset_minutes: Number(parse.tz_offset_minutes ?? 0),
          on_error: parse.on_error || 'warn',
          ...(parse.format ? { format: parse.format } : {}),
        },
      };
    })
    ;

  // The primary date is the one the server marked `recommended`. It drives every
  // range filter, the ordering and the month bucketing, so getting it wrong
  // silently drops records out of "last month" — and between `publish_date_time`
  // and `modified_date`, both on 100% of records, nothing derivable here can
  // choose. Fallback is best coverage, never list position.
  const primaryDate =
    pickRecommended(dates) ||
    (dates.length
      ? [...dates].sort((a, b) => b.coveragePct - a.coveragePct)[0]
      : null);
  if (primaryDate) primaryDate.primary = true;
  dates.forEach((d) => {
    delete d.coveragePct;
    delete d.recommended;
    delete d.serverFlagged;
  });

  // The text fields the server marked `recommended` — NOT everything it offered.
  // A wide export offers dozens of text-ish columns, and taking the lot both
  // blows the server's 12-field cap and assembles a document nobody asked for.
  // The rest stay offered as unticked rows.
  //
  // What still differs per included field is `embed`, the setting that costs
  // money: a `large` body arrives stored-but-not-vectorised.
  const text = pickAllRecommended(
    textCandidates,
    // Only on a response that flags nothing: the title-shaped field, so
    // `text: []` is still never saved.
    (candidate) => candidate.role === 'title'
  )
    .slice(0, MAX_TEXT_FIELDS)
    .map((c, i) => textFromCandidate(c, i));
  // At least one field embedded, or nothing is findable by meaning.
  if (text.length && !text.some((t) => t.embed)) text[0].embed = true;

  const rawRelations = Array.isArray(suggestions.relations) ? suggestions.relations : [];
  const relationNames = new Set();
  const relations = rawRelations.map((entry) => {
    const path = typeof entry === 'string' ? entry : entry?.path || '';
    return {
      name: uniqueName(slugifyFacetName(entry?.name || pathLeafName(path)), relationNames),
      path,
      target: 'self',
    };
  });

  const labels = guessRecordLabels(label);
  const mapping = {
    version: 1,
    dataset: {
      key: key || '',
      label: label || '',
      description: description || null,
      record_label_singular: labels.singular,
      record_label_plural: labels.plural,
    },
    record_key: serializeRecordKey(recordKeyPaths),
    title,
    text,
    dates,
    // Filled in below, once the rest of the mapping exists to judge against.
    facets: [],
    relations,
    ignore: Array.isArray(suggestions.ignore) ? suggestions.ignore : [],
    limits: {
      max_text_chars: 400000,
      max_facet_values_per_record: 64,
      max_value_len: 500,
    },
  };

  // The facets the server marked `recommended`, ticked and ready.
  //
  // Not the whole shortlist: that would hand the model the row identity (one
  // value per record, so filtering on it returns one), two raw timestamps and
  // two copies of the same taxonomy under different names. Each costs prompt
  // space and makes the filters that matter harder to find. The rest stay
  // visible and one click from being added.
  //
  // This runs last because a candidate is only redundant relative to the REST of
  // the mapping: `publish_date_time` is a poor facet precisely because it is
  // already a date, and that is only known once the dates are chosen.
  mapping.facets = pickAllRecommended(
    facetCandidatesFromInspect(inspection, mapping),
    // Only on a response that flags nothing: the ones nothing objects to, so the
    // wizard still opens with a working default.
    (candidate) => !candidate.reason
  )
    // Capped so a server that recommends more than it accepts cannot hand the
    // author a config that is refused the moment they press Create.
    .slice(0, MAX_FACETS)
    .map((candidate, index) => facetFromCandidate(candidate, index));

  // The paths above are only valid under the preprocessing that produced them,
  // so the two travel together from here on.
  if (preprocessing) mapping.preprocessing = preprocessing;

  return mapping;
};

/**
 * Everything from an /inspect response worth saying out loud, in one shape.
 * Truncation is included deliberately: coverage describes what was inspected,
 * and a reader who takes it for the whole dataset is being misled.
 */
export const readInspection = (inspection) => {
  if (!inspection) return null;
  const warnings = Array.isArray(inspection.warnings) ? inspection.warnings : [];
  const failures = Array.isArray(inspection.failures) ? inspection.failures : [];
  const fields = fieldsFromInspect(inspection);
  return {
    fields,
    failures,
    warnings,
    preview: Array.isArray(inspection.preview) ? inspection.preview : [],
    preprocessing: inspection.preprocessing || null,
    suggestions: inspection.suggestions || {},
    combinedFields: combinedFieldsFromInspect(inspection),
    // Read from the response, never assumed — it can be `normalized_2`.
    namespace: inspection.preprocessing?.namespace || PREPROCESSING_NAMESPACE,
    // `counts.total` is where the server actually reports it.
    inspectedCount: Number(
      firstDefined(
        inspection,
        ['inspected', 'sample_count', 'record_count'],
        inspection?.counts?.total ?? 0
      )
    ),
    emptyFields: fields.filter((f) => f.coveragePct === 0),
    quotedFields: fields.filter((f) => f.needsQuoting),
    derivedFields: fields.filter((f) => f.derived),
    // The server says so explicitly when it can; a warning mentioning truncation
    // is the fallback, since eating that silently is the failure mode here.
    // Deliberately NOT matching "limit" — the mapping limits (max_value_len and
    // friends) are warned about too, and reading one of those as "we only looked
    // at part of your file" is its own wrong answer.
    truncated:
      Boolean(firstDefined(inspection, ['truncated', 'was_truncated'], false)) ||
      warnings.some((w) => /truncat|capped|only the first/i.test(String(w))),
  };
};

// ── Duplicate record keys (dry-run and push reports) ───────────────────────

/**
 * A push keeps the LAST record for each key, so duplicates are silent data
 * loss — the normal failure on an event export where an entity id repeats
 * across events. Reported by both dry-run and push; absent on an older server,
 * in which case this returns null and nothing is claimed either way.
 *
 * @returns {null|{unique: number|null, duplicateCount: number, rows: Array, groups: Array}}
 */
export const readDuplicateReport = (report) => {
  if (!report || typeof report !== 'object') return null;
  const hasKeys =
    report.unique_record_keys !== undefined ||
    report.duplicate_record_count !== undefined ||
    report.duplicate_record_keys !== undefined;
  if (!hasKeys) return null;

  const rows = Array.isArray(report.duplicate_record_keys) ? report.duplicate_record_keys : [];
  const byKey = new Map();
  rows.forEach((row) => {
    const key = String(row?.record_key ?? '');
    if (!byKey.has(key)) {
      byKey.set(key, { key, firstIndex: row?.first_index, indexes: [] });
    }
    byKey.get(key).indexes.push(row?.index);
  });

  return {
    unique:
      report.unique_record_keys === undefined ? null : Number(report.unique_record_keys),
    duplicateCount: Number(report.duplicate_record_count || 0),
    rows,
    // Ordered worst-first: the key repeating twenty times is the one to look at.
    groups: [...byKey.values()].sort((a, b) => b.indexes.length - a.indexes.length),
  };
};

/**
 * How much of a record's text is being embedded.
 *
 * The denominator is `full_text_chars` — the actual length of the document.
 * `total_text_chars` is a read-tier planner input, NOT document length, and
 * using it produced ratios over 100% on records where everything was embedded.
 * It is still read as a fallback so an older response renders something sane.
 */
export const readEmbedRatio = (previewItem) => {
  const embedded = Number(previewItem?.embed_text_chars || 0);
  const full = Number(
    previewItem?.full_text_chars ?? previewItem?.total_text_chars ?? 0
  );
  if (!full) return { embedded, full: 0, pct: null, allEmbedded: false };
  const allEmbedded = embedded >= full;
  return {
    embedded,
    full,
    pct: Math.min(100, Math.round((embedded / full) * 100)),
    allEmbedded,
  };
};

// ── Whole-file duplicate-key check ─────────────────────────────────────────
// The ONLY thing still derived from the raw sample in the browser. Schema
// sniffing and mapping proposals both moved to POST /kg-datasets/inspect, which
// sees the data through the same decoder the push does — guessing either here
// produced paths the server would not agree with.
//
// This stays because it answers a question no single server response can: the
// dry-run report covers the sampled records and a push report covers one batch
// of 500, but a key that repeats across batch 3 and batch 40 is invisible to
// both. The whole file is sitting in memory here, so it is one pass to know.

const blank = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === 'string' && v.trim() === '') ||
  (Array.isArray(v) && v.length === 0);

const readPathValue = (record, segments) => {
  let current = [record];
  for (const segment of segments) {
    const next = [];
    current.forEach((node) => {
      if (node === null || node === undefined) return;
      const value = node[segment.name];
      if (value === undefined) return;
      if (Array.isArray(value)) next.push(...value);
      else next.push(value);
    });
    current = next;
  }
  return current.filter((v) => !blank(v));
};

/**
 * Does this record_key actually identify a row, or does it collapse the file?
 *
 * The upsert key is (dataset, record_key), so two records sharing a key are ONE
 * record — the second silently overwrites the first. Pick a column like `source`
 * on an event feed and 50 records become 6; push 100k and keep 6.
 *
 * The server reports duplicates too, in every dry-run and push report — but
 * only over the records IN that request. This runs over the whole file, which is
 * the only way to catch a key that repeats across two different batches.
 *
 * Only valid on paths that exist in the raw record. When `record_key` points
 * into the preprocessing namespace the value does not exist until the server
 * derives it, so callers must check `recordKeyIsDerived()` first and fall back
 * to the server's `duplicate_record_keys[]` instead.
 *
 * @returns {null|{total, keyed, missing, distinct, lost, collapsed, worst}}
 */
export const analyzeRecordKeys = (samples, recordKeyValue) => {
  const records = Array.isArray(samples) ? samples : [];
  const paths = parseRecordKey(recordKeyValue);
  if (records.length === 0 || paths.length === 0) return null;

  const segmentLists = paths.map((path) => parsePath(path));
  const counts = new Map();
  let missing = 0;

  records.forEach((record) => {
    const parts = segmentLists.map((segments) => readPathValue(record, segments));
    // A record whose key column is empty cannot be keyed at all — the API skips
    // it with "no record_key", which is a different failure worth separating.
    if (parts.some((values) => values.length === 0)) {
      missing += 1;
      return;
    }
    const key = parts.map((values) => String(values[0])).join(KEY_PART_SEPARATOR);
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  const keyed = records.length - missing;
  const distinct = counts.size;
  const worst = [...counts.entries()]
    .filter(([, n]) => n > 1)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([key, n]) => ({ key: key.split(KEY_PART_SEPARATOR).join(' + '), count: n }));

  return {
    total: records.length,
    keyed,
    missing,
    distinct,
    lost: Math.max(0, keyed - distinct),
    collapsed: distinct < keyed,
    worst,
  };
};
export const humanizeLabel = (raw) =>
  String(raw ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

// The agent says these words out loud in its answers — "12 articles", not
// "12 records" — so a mangled guess is visible to every end user. It is only a
// default (the mapping editor shows a live preview and lets it be edited), but
// "12 new" or "12 newss" reads as a bug, so the two sets below earn their keep.

/** No plural form at all: one news, many news. */
const UNCOUNTABLE = new Set([
  'news', 'data', 'info', 'information', 'series', 'species', 'equipment',
  'software', 'research', 'feedback', 'correspondence', 'guidance',
]);

/** Ends in "s" but is already singular — de-pluralising gives "statu", "basi". */
const ALREADY_SINGULAR = new Set([
  'status', 'basis', 'analysis', 'thesis', 'crisis', 'corpus', 'census',
  'bonus', 'campus', 'focus', 'virus', 'synopsis',
]);

const pluralize = (word) => {
  if (UNCOUNTABLE.has(word)) return word;
  // analysis → analyses, basis → bases, crisis → crises.
  if (ALREADY_SINGULAR.has(word) && /is$/.test(word)) return `${word.slice(0, -2)}es`;
  if (/(s|ss|sh|ch|x|z)$/.test(word)) return `${word}es`;
  if (/[^aeiou]y$/.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
};

const singularize = (word) => {
  if (UNCOUNTABLE.has(word) || ALREADY_SINGULAR.has(word)) return word;
  if (/ies$/.test(word) && word.length > 4) return `${word.slice(0, -3)}y`;
  if (/sses$/.test(word)) return word.slice(0, -2);
  if (/s$/.test(word) && !/ss$/.test(word)) {
    const singular = word.slice(0, -1);
    // Anything this short after trimming was almost certainly never a plural.
    return singular.length <= 3 ? word : singular;
  }
  return word;
};

const guessRecordLabels = (label) => {
  const words = String(label || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const last = words.length
    ? words[words.length - 1].toLowerCase().replace(/[^a-z]/g, '')
    : '';
  if (!last) return { singular: 'record', plural: 'records' };
  const singular = singularize(last);
  // If we trimmed a plural, the word we started from IS the plural.
  return { singular, plural: singular === last ? pluralize(last) : last };
};

/**
 * The column vocabulary a SAVED mapping config already implies.
 *
 * The real vocabulary comes from POST /inspect, which needs raw sample records
 * — and those live only in this browser session. Reopening a configured dataset
 * (or just refreshing) therefore left every column picker with an empty list,
 * which reads as "this dataset has no columns" rather than "no sample is
 * loaded". These paths are the ones the config is already built on, so offering
 * them keeps the pickers usable for editing existing mappings.
 *
 * Deliberately carries NO coverage, types or example values: none of that is
 * knowable without a sample, and `coveragePct` is left undefined so it renders
 * as "—" instead of a fabricated 0%.
 *
 * @returns {Array} field-shaped entries, same contract as fieldsFromInspect()
 */
export const fieldsFromMappingConfig = (config) => {
  if (!config || typeof config !== 'object') return [];
  const paths = [];
  const add = (value) => {
    const raw = String(value ?? '').trim();
    if (raw) paths.push(normalizePath(raw));
  };

  parseRecordKey(config.record_key).forEach(add);
  add(config.title);
  ['text', 'dates', 'facets', 'relations'].forEach((section) => {
    if (!Array.isArray(config[section])) return;
    config[section].forEach((entry) => add(entry?.path));
  });
  (config.preprocessing?.decode || []).forEach((entry) => add(entry?.path));
  (config.preprocessing?.fields || []).forEach((field) => {
    (field?.candidates || []).forEach((candidate) => add(candidate?.path));
    // A derived field is addressed by its namespaced name, which is what the
    // other sections reference it as.
    const name = String(field?.name || '').trim();
    if (name) add(`${config.preprocessing?.namespace || PREPROCESSING_NAMESPACE}.${name}`);
  });

  return [...new Set(paths)].map((path) => {
    const segments = parsePath(path);
    return {
      path,
      segments,
      leaf: segments.length ? segments[segments.length - 1].name : path,
      depth: segments.length,
      isList: segments.some((s) => s.isList),
      multiValued: segments.some((s) => s.isList),
      coveragePct: undefined,
      distinct: null,
      values: [],
      types: [],
      type: '',
      distinctTruncated: false,
      derived: false,
      coveredBy: null,
      source: null,
      needsQuoting: segments.some((s) => segmentNeedsQuoting(s.name)),
      // Marks the entry as inferred rather than observed, so nothing downstream
      // reports it as something seen in the data.
      fromConfig: true,
    };
  });
};

// ── Mapping config normalisation ───────────────────────────────────────────

/**
 * Re-quote every path in a config. The JSON paste-in is the one route that
 * bypasses the path pickers, so it runs through here — and the result is shown
 * as a reviewable diff rather than applied silently.
 * @returns {{config, changed: Array<{where: string, from: string, to: string}>}}
 */
export const normalizeMappingPaths = (config) => {
  const changed = [];
  if (!config || typeof config !== 'object') return { config, changed };
  const next = JSON.parse(JSON.stringify(config));

  const fix = (value, where) => {
    const raw = String(value ?? '').trim();
    if (!raw) return raw;
    const normalized = normalizePath(raw);
    if (normalized !== raw) changed.push({ where, from: raw, to: normalized });
    return normalized;
  };

  const keys = parseRecordKey(next.record_key);
  if (keys.length) {
    next.record_key = serializeRecordKey(keys.map((k, i) => fix(k, `record_key[${i}]`)));
  }
  if (next.title) next.title = fix(next.title, 'title');

  ['text', 'dates', 'facets', 'relations'].forEach((section) => {
    if (!Array.isArray(next[section])) return;
    next[section].forEach((entry, index) => {
      if (entry && entry.path) {
        entry.path = fix(entry.path, `${section}[${index}].path`);
      }
    });
  });

  // Preprocessing reads raw columns, so its paths need the same quoting — and a
  // JSON-string column is exactly the kind of thing that arrives named
  // `event.payload`.
  if (next.preprocessing && typeof next.preprocessing === 'object') {
    (next.preprocessing.decode || []).forEach((entry, index) => {
      if (entry?.path) entry.path = fix(entry.path, `preprocessing.decode[${index}].path`);
    });
    (next.preprocessing.fields || []).forEach((field, index) => {
      (field?.candidates || []).forEach((candidate, c) => {
        if (candidate?.path) {
          candidate.path = fix(
            candidate.path,
            `preprocessing.fields[${index}].candidates[${c}].path`
          );
        }
      });
    });
  }

  return { config: next, changed };
};

/**
 * Client-side mirror of the API's validation, so a mistake is caught before a
 * round trip. Returns a flat list of human-readable problems.
 */
export const validateMappingConfig = (config, { datasetKey } = {}) => {
  const errors = [];
  if (!config || typeof config !== 'object') return ['The mapping config is empty.'];

  if (datasetKey && config.dataset?.key !== datasetKey) {
    errors.push(`The config's dataset.key must be "${datasetKey}".`);
  }

  const recordKeyError = validateRecordKey(parseRecordKey(config.record_key));
  if (recordKeyError) errors.push(recordKeyError);

  errors.push(...validatePreprocessing(config));

  // A derived record_key that names a field the derivation doesn't produce is a
  // 400 on save and, worse, reads as a working config in the form.
  if (hasPreprocessing(config)) {
    const derivedNames = new Set(
      (config.preprocessing?.fields || []).map((f) => String(f?.name || '').trim())
    );
    const namespace = preprocessingNamespace(config);
    [
      ...parseRecordKey(config.record_key).map((p) => ({ path: p, where: 'The row identity' })),
      ...(config.title ? [{ path: config.title, where: 'The title' }] : []),
      ...(config.facets || []).map((f) => ({ path: f?.path, where: `Facet "${f?.name}"` })),
      ...(config.dates || []).map((d) => ({ path: d?.path, where: `Date "${d?.name}"` })),
      ...(config.text || []).map((t) => ({ path: t?.path, where: `Text field "${t?.label || t?.path}"` })),
    ].forEach(({ path, where }) => {
      if (!path || !isDerivedPath(config, path)) return;
      const name = parsePath(path)[1]?.name;
      if (name && !derivedNames.has(name)) {
        errors.push(
          `${where} reads ${namespace}.${name}, but input normalization doesn't derive a field called "${name}".`
        );
      }
    });
  }

  const text = Array.isArray(config.text) ? config.text : [];
  if (text.length > MAX_TEXT_FIELDS) {
    errors.push(
      `${text.length} text fields — the maximum is ${MAX_TEXT_FIELDS}. Remove ${text.length - MAX_TEXT_FIELDS}; every one of them is assembled into the document the model reads, so a shorter list also answers better.`
    );
  }
  if (text.length && !text.some((t) => t.embed)) {
    errors.push(
      'At least one text field must be embedded, or nothing in this dataset is findable by meaning.'
    );
  }
  text.forEach((t, i) => {
    if (!String(t?.path || '').trim()) errors.push(`Text field ${i + 1} has no column.`);
  });

  const dates = Array.isArray(config.dates) ? config.dates : [];
  const primaries = dates.filter((d) => d?.primary);
  if (dates.length && primaries.length !== 1) {
    errors.push(
      primaries.length === 0
        ? 'Exactly one date must be marked primary — it drives date filters, ordering and month bucketing.'
        : 'Only one date can be marked primary.'
    );
  }

  const facets = Array.isArray(config.facets) ? config.facets : [];
  if (facets.length > MAX_FACETS) {
    errors.push(
      `${facets.length} facets — the maximum is ${MAX_FACETS}. Drop ${facets.length - MAX_FACETS}; every facet is also text the model reads before it can pick one, so a shorter list answers better as well.`
    );
  }
  const facetNames = new Set();
  facets.forEach((f, i) => {
    const name = String(f?.name || '');
    if (!FACET_NAME_RE.test(name)) {
      errors.push(
        `Facet ${i + 1}: name "${name}" must be lowercase letters, digits and underscores, starting with a letter.`
      );
    }
    if (facetNames.has(name)) errors.push(`Two facets are both named "${name}".`);
    facetNames.add(name);
    if (!String(f?.path || '').trim()) errors.push(`Facet "${name || i + 1}" has no column.`);
  });

  return errors;
};

/** The (path, embed) pairs that are frozen once records exist. */
export const textSignature = (config) =>
  JSON.stringify(
    (Array.isArray(config?.text) ? config.text : [])
      .map((t) => [String(t?.path || ''), Boolean(t?.embed)])
      .sort((a, b) => a[0].localeCompare(b[0]))
  );

export const textConfigChanged = (a, b) => textSignature(a) !== textSignature(b);

// ── Combined fields (suggestions.combined_fields[]) ────────────────────────
// One logical field spread across several columns — the email is `traits.email`
// on one source, `contact_email` on another, `email` on a third. Each column
// alone covers a third of the records; combined they cover all of them.
//
// A combined field is a `preprocessing.fields[]` entry with mode `first`: the
// first candidate that has a value wins. Never `join` — that glues every value
// together, so a record with the email in three places would store
// "a@x.com | a@x.com | a@x.com".

export const COMBINED_USES = [
  { value: 'filter', label: 'Filter on it', hint: 'Becomes a facet the agent can count and group by.' },
  { value: 'date', label: 'Primary date', hint: 'Drives date filters, ordering and month bucketing.' },
  { value: 'identity', label: 'Row identity', hint: 'Becomes the upsert key — one record per value.' },
];

/** The suggestions, normalised. Candidates keep the server's listed order. */
export const combinedFieldsFromInspect = (inspection) => {
  const raw = inspection?.suggestions?.combined_fields;
  return (Array.isArray(raw) ? raw : [])
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') return null;
      const candidates = (Array.isArray(entry.candidates) ? entry.candidates : [])
        .map((c) => {
          const path = typeof c === 'string' ? c : c?.path;
          if (!path) return null;
          const meta = typeof c === 'object' ? c : {};
          return {
            path,
            populatedRecords: Number(meta.populated_records || 0),
            coveragePct: Number(meta.coverage_pct || 0),
            examples: Array.isArray(meta.examples) ? meta.examples : [],
            recommended: Boolean(meta.recommended),
            // Why an unticked candidate is unticked — "belongs to a sender".
            reason: meta.reason || null,
          };
        })
        .filter(Boolean);
      return {
        id: `suggested-${entry.kind || 'field'}-${index}`,
        kind: entry.kind || '',
        suggestedName: entry.suggested_name || entry.field?.name || `combined_${index + 1}`,
        savedPath: entry.saved_path || '',
        useAs: entry.use_as || 'filter',
        candidates,
        populatedRecords: Number(entry.populated_records || 0),
        coveragePct: Number(entry.coverage_pct || 0),
        bestSinglePopulatedRecords: Number(entry.best_single_populated_records || 0),
        bestSingleCoveragePct: Number(entry.best_single_coverage_pct || 0),
        recommended: Boolean(entry.recommended),
        // Ready to save as-is when the author changes nothing.
        field: entry.field || null,
        dateSpec: entry.date_spec || null,
        recordKey: entry.record_key ?? null,
      };
    })
    .filter(Boolean);
};

/**
 * Editable drafts for the wizard step. Ticked by default exactly where the
 * server says `recommended` — the entry, and each candidate within it.
 */
export const combinedDraftsFromSuggestions = (suggestions) =>
  (suggestions || []).map((s) => ({
    ...s,
    name: s.suggestedName,
    enabled: s.recommended,
    custom: false,
    candidates: s.candidates.map((c) => ({ ...c, ticked: c.recommended, added: false })),
  }));

export const emptyCombinedDraft = () => ({
  id: `custom-${Date.now()}-${Math.round(Math.random() * 1e6)}`,
  kind: '',
  name: '',
  suggestedName: '',
  savedPath: '',
  useAs: 'filter',
  candidates: [],
  enabled: true,
  custom: true,
  field: null,
  dateSpec: null,
  recordKey: null,
});

const tickedPaths = (draft) => draft.candidates.filter((c) => c.ticked).map((c) => c.path);

/** Where the combined value lives: `<namespace>.<name>`. */
export const combinedSavedPath = (draft, namespace = PREPROCESSING_NAMESPACE) => {
  const name = String(draft.name || '').trim();
  if (!name) return '';
  // The server's own path, unless the author renamed it.
  if (!draft.custom && draft.savedPath && name === draft.suggestedName) return draft.savedPath;
  return buildPath([{ name: namespace }, { name }]);
};

/**
 * The `preprocessing.fields[]` entry to save.
 *
 * Untouched → the server's `field` EXACTLY as returned (it may carry `case`,
 * `when`, `select` the form never shows). Edited → rebuilt from the ticked
 * candidates in the listed order, keeping any per-candidate options the server
 * sent for a path that is still ticked.
 */
export const buildCombinedField = (draft) => {
  const ticked = tickedPaths(draft);
  const name = String(draft.name || '').trim();
  const source = draft.field;
  const sourcePaths = (source?.candidates || []).map((c) => c?.path);
  const untouched =
    source &&
    !draft.custom &&
    name === source.name &&
    ticked.length === sourcePaths.length &&
    ticked.every((path, i) => path === sourcePaths[i]);
  if (untouched) return source;

  const extras = new Map((source?.candidates || []).map((c) => [c?.path, c]));
  return {
    ...(source || {}),
    name,
    mode: 'first',
    candidates: ticked.map((path) => extras.get(path) || { path }),
  };
};

/** A draft is worth saving once it is switched on and reads from something. */
export const isUsableCombinedDraft = (draft) =>
  Boolean(draft?.enabled) &&
  tickedPaths(draft).length > 0 &&
  String(draft.name || '').trim() !== '';

/** Per-draft problems, keyed by draft id. */
export const combinedDraftProblems = (drafts) => {
  const problems = {};
  const seen = new Set();
  (drafts || []).forEach((draft) => {
    if (!draft.enabled) return;
    const name = String(draft.name || '').trim();
    const list = [];
    if (!name) list.push('Give it a name.');
    else if (!FACET_NAME_RE.test(name)) {
      list.push('Lowercase letters, digits and underscores, starting with a letter.');
    } else if (seen.has(name)) list.push(`Another combined field is already called “${name}”.`);
    else seen.add(name);
    const count = tickedPaths(draft).length;
    if (count === 0) list.push('Tick at least one field to read from.');
    else if (draft.custom && count === 1) {
      list.push('One field is not a combination — add another, or use that field directly.');
    }
    if (list.length) problems[draft.id] = list;
  });
  return problems;
};

/**
 * The preprocessing block to send to /inspect and save: the base block with
 * every usable draft appended, replacing any existing entry of the same name.
 */
export const preprocessingWithCombined = (basePreprocessing, drafts) => {
  const base = basePreprocessing
    ? JSON.parse(JSON.stringify(basePreprocessing))
    : emptyPreprocessing();
  const built = (drafts || []).filter(isUsableCombinedDraft).map(buildCombinedField);
  const names = new Set(built.map((f) => f.name));
  base.fields = [...(base.fields || []).filter((f) => !names.has(f?.name)), ...built];
  if (!Array.isArray(base.decode)) base.decode = [];
  if (!base.namespace) base.namespace = PREPROCESSING_NAMESPACE;
  return base;
};

/**
 * Wire each usable combined field into the mapping by its `use_as`:
 *   filter   → a facet on its saved path
 *   date     → `date_spec` as the primary date
 *   identity → `record_key` set to the entry's record_key
 *
 * Mutates `mapping`. Returns the facet candidates it created, so the facet
 * chooser can list them — a ticked facet the chooser cannot see would be
 * dropped the next time anything in the list is toggled.
 */
export const wireCombinedFields = (mapping, drafts, namespace = PREPROCESSING_NAMESPACE) => {
  const created = [];
  (drafts || []).filter(isUsableCombinedDraft).forEach((draft) => {
    const name = String(draft.name).trim();
    const path = combinedSavedPath(draft, namespace);
    const renamed = draft.custom || name !== draft.suggestedName;

    if (draft.useAs === 'identity') {
      const key = !renamed && draft.recordKey != null ? draft.recordKey : path;
      const paths = Array.isArray(key) ? key : Array.isArray(key?.paths) ? key.paths : [key];
      mapping.record_key = serializeRecordKey(paths);
      return;
    }

    if (draft.useAs === 'date') {
      const spec = (!renamed && draft.dateSpec) || {};
      const date = {
        name: spec.name || name,
        path: spec.path || path,
        primary: true,
        parse: {
          kind: 'auto',
          unit: 'auto',
          tz_offset_minutes: 0,
          on_error: 'warn',
          ...(spec.parse || {}),
        },
      };
      mapping.dates = [
        date,
        ...(mapping.dates || [])
          .filter((d) => d.path !== date.path)
          .map((d) => ({ ...d, primary: false })),
      ];
      return;
    }

    // filter
    const taken = new Set(
      (mapping.facets || []).filter((f) => f.path !== path).map((f) => f.name)
    );
    const candidate = {
      path,
      name: uniqueName(slugifyFacetName(name), taken),
      label: humanizeLabel(name),
      kind: 'scalar',
      cardinality: 'one',
      coveragePct: draft.coveragePct || 0,
      distinct: 0,
      values: draft.candidates
        .filter((c) => c.ticked)
        .flatMap((c) => c.examples)
        .slice(0, 3),
      note: '',
      recommended: true,
      serverFlagged: true,
      reason: null,
      combined: true,
      order: -1,
    };
    const others = (mapping.facets || []).filter((f) => f.path !== path);
    mapping.facets = [facetFromCandidate(candidate, 0), ...others].map((f, i) => ({
      ...f,
      order_index: i + 1,
    }));
    created.push(candidate);
  });
  return created;
};

// ── first vs join ──────────────────────────────────────────────────────────
// `first` is for ONE fact stored under different names (email / contact_email /
// customer.email): take the first value found. `join` is for DIFFERENT parts
// that belong together (first + last name, address lines, tag fields). Joining
// same-kind columns just repeats the value: "a@x.com a@x.com a@x.com".

export const DERIVED_MODE_HINT =
  'First: same information under different names, uses the first one found. Join: different parts to glue together, e.g. first + last name.';

const VALUE_KINDS = [
  { kind: 'email', re: /(^|_)e?mail(_|$)|email/i },
  { kind: 'phone', re: /(phone|mobile|msisdn|(^|_)tel(_|$))/i },
  { kind: 'date', re: /(date|time|timestamp|(^|_)(at|on|ts)$)/i },
  { kind: 'id', re: /((^|_)(id|uuid|key)$|_id(_|$))/i },
  { kind: 'url', re: /(url|link|href)/i },
];

const valueKindOf = (path) => {
  const leaf = pathLeafName(path);
  const hit = VALUE_KINDS.find((k) => k.re.test(leaf));
  return hit ? hit.kind : null;
};

/**
 * Why a `join` field is probably a mistake, or null. Two cases, both advice
 * rather than a block — join is legitimate for different parts of one value:
 *   • every candidate holds the same kind of value → joining repeats it;
 *   • the field is used as the row identity → a multi-column key belongs in the
 *     record_key list, which is built so two different keys never collide.
 */
export const joinFieldWarnings = (field, config) => {
  if (!field || field.mode !== 'join') return [];
  const out = [];
  const paths = (field.candidates || []).map((c) => c?.path).filter(Boolean);
  if (paths.length >= 2) {
    const kinds = paths.map(valueKindOf);
    if (kinds[0] && kinds.every((k) => k === kinds[0])) {
      out.push(
        `These all look like ${kinds[0] === 'id' ? 'identifiers' : `${kinds[0]}s`} — joining will repeat the same value. Use First unless they really are different parts.`
      );
    }
  }
  const name = String(field.name || '').trim();
  if (name && config) {
    const usedAsKey = parseRecordKey(config.record_key).some(
      (p) => isDerivedPath(config, p) && parsePath(p)[1]?.name === name
    );
    if (usedAsKey) {
      out.push(
        'This is used as the row identity. For a key made of several columns (order id + line number), pick them as separate columns in the identity picker instead — that list is built so two different keys can never come out the same, which a joined string cannot promise.'
      );
    }
  }
  return out;
};
